/**
 * AudioProcessor - Real-time audio EQ, compression & de-essing for voice playback
 * Singleton that wraps a shared AudioContext and connects to HTMLAudioElements.
 */

export type AudioMode = 'default' | 'warm' | 'clear' | 'kids';

interface ModeProfile {
  highPassFreq: number;
  lowShelfFreq: number;
  lowShelfGain: number;
  peakingFreq: number;
  peakingGain: number;
  highShelfFreq: number;
  highShelfGain: number;
  deEsserGain: number;
  compressorThreshold: number;
  compressorRatio: number;
  compressorKnee: number;
  compressorAttack: number;
  compressorRelease: number;
}

const MODE_PROFILES: Record<AudioMode, ModeProfile> = {
  default: {
    highPassFreq: 80,
    lowShelfFreq: 200,
    lowShelfGain: 0,
    peakingFreq: 2500,
    peakingGain: 0,
    highShelfFreq: 4500,
    highShelfGain: -2,
    deEsserGain: -4,
    compressorThreshold: -20,
    compressorRatio: 2.5,
    compressorKnee: 25,
    compressorAttack: 0.003,
    compressorRelease: 0.25,
  },
  warm: {
    highPassFreq: 80,
    lowShelfFreq: 250,
    lowShelfGain: 3,
    peakingFreq: 2500,
    peakingGain: -1,
    highShelfFreq: 4000,
    highShelfGain: -5,
    deEsserGain: -8,
    compressorThreshold: -18,
    compressorRatio: 3,
    compressorKnee: 20,
    compressorAttack: 0.003,
    compressorRelease: 0.25,
  },
  clear: {
    highPassFreq: 120,
    lowShelfFreq: 200,
    lowShelfGain: -2,
    peakingFreq: 2000,
    peakingGain: 3,
    highShelfFreq: 5000,
    highShelfGain: -3,
    deEsserGain: -6,
    compressorThreshold: -15,
    compressorRatio: 2,
    compressorKnee: 25,
    compressorAttack: 0.003,
    compressorRelease: 0.2,
  },
  kids: {
    highPassFreq: 80,
    lowShelfFreq: 200,
    lowShelfGain: 2,
    peakingFreq: 2000,
    peakingGain: 0,
    highShelfFreq: 3500,
    highShelfGain: -7,
    deEsserGain: -10,
    compressorThreshold: -20,
    compressorRatio: 4,
    compressorKnee: 15,
    compressorAttack: 0.003,
    compressorRelease: 0.3,
  },
};

interface ConnectedElement {
  source: MediaElementAudioSourceNode;
  highPass: BiquadFilterNode;
  lowShelf: BiquadFilterNode;
  peaking: BiquadFilterNode;
  highShelf: BiquadFilterNode;
  deEsser: BiquadFilterNode;
  deEsserHigh: BiquadFilterNode;
  compressor: DynamicsCompressorNode;
}

export class AudioProcessor {
  private static _instance: AudioProcessor;
  private ctx: AudioContext | null = null;
  private connected = new Map<HTMLAudioElement, ConnectedElement>();
  private currentMode: AudioMode = 'warm';
  private deEsserEnabled = true;

  static instance(): AudioProcessor {
    if (!this._instance) {
      this._instance = new AudioProcessor();
    }
    return this._instance;
  }

  /**
   * Provide an existing AudioContext (e.g. from MediaUnlocker)
   */
  setContext(ctx: AudioContext) {
    this.ctx = ctx;
  }

  private getOrCreateContext(): AudioContext {
    if (this.ctx) return this.ctx;
    const Ctor = window.AudioContext || (window as any).webkitAudioContext;
    this.ctx = new Ctor();
    return this.ctx;
  }

  getMode(): AudioMode {
    return this.currentMode;
  }

  isDeEsserEnabled(): boolean {
    return this.deEsserEnabled;
  }

  /**
   * Connect an HTMLAudioElement through the processing chain.
   * Safe to call multiple times for the same element (no-op if already connected).
   * The element MUST have crossOrigin = "anonymous" set before loading.
   */
  connectElement(audio: HTMLAudioElement): void {
    if (this.connected.has(audio)) return;

    // Ensure crossOrigin is set (required for Web Audio API processing)
    if (!audio.crossOrigin) {
      audio.crossOrigin = 'anonymous';
    }

    try {
      const ctx = this.getOrCreateContext();
      
      // Resume context if suspended (critical for iOS Safari)
      if (ctx.state === 'suspended') {
        ctx.resume().catch((e) => console.warn('🎛️ AudioProcessor: context resume failed:', e));
      }
      
      // Log context state for debugging
      console.log(`🎛️ AudioProcessor: context state="${ctx.state}", sampleRate=${ctx.sampleRate}`);
      
      const profile = MODE_PROFILES[this.currentMode];

      // Create source node (can only be done once per element)
      const source = ctx.createMediaElementSource(audio);

      // High-pass filter (rumble cut)
      const highPass = ctx.createBiquadFilter();
      highPass.type = 'highpass';
      highPass.frequency.value = profile.highPassFreq;
      highPass.Q.value = 0.7;

      // Low-shelf (warmth)
      const lowShelf = ctx.createBiquadFilter();
      lowShelf.type = 'lowshelf';
      lowShelf.frequency.value = profile.lowShelfFreq;
      lowShelf.gain.value = profile.lowShelfGain;

      // Peaking (mid presence)
      const peaking = ctx.createBiquadFilter();
      peaking.type = 'peaking';
      peaking.frequency.value = profile.peakingFreq;
      peaking.Q.value = 1.5;
      peaking.gain.value = profile.peakingGain;

      // High-shelf (harshness control)
      const highShelf = ctx.createBiquadFilter();
      highShelf.type = 'highshelf';
      highShelf.frequency.value = profile.highShelfFreq;
      highShelf.gain.value = profile.highShelfGain;

      // De-esser low band (peaking notch around 5.5kHz — primary sibilance)
      const deEsser = ctx.createBiquadFilter();
      deEsser.type = 'peaking';
      deEsser.frequency.value = 5500;
      deEsser.Q.value = 2;
      deEsser.gain.value = this.deEsserEnabled ? profile.deEsserGain : 0;

      // De-esser high band (peaking notch around 7.5kHz — upper sibilance)
      const deEsserHigh = ctx.createBiquadFilter();
      deEsserHigh.type = 'peaking';
      deEsserHigh.frequency.value = 7500;
      deEsserHigh.Q.value = 2;
      deEsserHigh.gain.value = this.deEsserEnabled ? (profile.deEsserGain * 0.7) : 0;

      // Compressor
      const compressor = ctx.createDynamicsCompressor();
      compressor.threshold.value = profile.compressorThreshold;
      compressor.ratio.value = profile.compressorRatio;
      compressor.knee.value = profile.compressorKnee;
      compressor.attack.value = profile.compressorAttack;
      compressor.release.value = profile.compressorRelease;

      // Chain: source → highPass → lowShelf → peaking → highShelf → deEsser → deEsserHigh → compressor → destination
      source.connect(highPass);
      highPass.connect(lowShelf);
      lowShelf.connect(peaking);
      peaking.connect(highShelf);
      highShelf.connect(deEsser);
      deEsser.connect(deEsserHigh);
      deEsserHigh.connect(compressor);
      compressor.connect(ctx.destination);

      this.connected.set(audio, { source, highPass, lowShelf, peaking, highShelf, deEsser, deEsserHigh, compressor });
      console.log(`🎛️ AudioProcessor: connected element (mode: ${this.currentMode})`);

    } catch (error) {
      console.warn('🎛️ AudioProcessor: failed to connect element:', error);
      // If connection fails (e.g. CORS), don't block playback —
      // the audio element still plays through default output
    }
  }

  /**
   * Update all connected filter chains to a new mode in real-time
   */
  setMode(mode: AudioMode): void {
    this.currentMode = mode;
    const profile = MODE_PROFILES[mode];

    this.connected.forEach((chain) => {
      chain.highPass.frequency.value = profile.highPassFreq;
      chain.lowShelf.frequency.value = profile.lowShelfFreq;
      chain.lowShelf.gain.value = profile.lowShelfGain;
      chain.peaking.frequency.value = profile.peakingFreq;
      chain.peaking.gain.value = profile.peakingGain;
      chain.highShelf.frequency.value = profile.highShelfFreq;
      chain.highShelf.gain.value = profile.highShelfGain;
      chain.deEsser.gain.value = this.deEsserEnabled ? profile.deEsserGain : 0;
      chain.deEsserHigh.gain.value = this.deEsserEnabled ? (profile.deEsserGain * 0.7) : 0;
      chain.compressor.threshold.value = profile.compressorThreshold;
      chain.compressor.ratio.value = profile.compressorRatio;
      chain.compressor.knee.value = profile.compressorKnee;
      chain.compressor.attack.value = profile.compressorAttack;
      chain.compressor.release.value = profile.compressorRelease;
    });

    console.log(`🎛️ AudioProcessor: mode set to "${mode}"`);
  }

  /**
   * Toggle de-esser on/off
   */
  setDeEsserEnabled(enabled: boolean): void {
    this.deEsserEnabled = enabled;
    const profile = MODE_PROFILES[this.currentMode];

    this.connected.forEach((chain) => {
      chain.deEsser.gain.value = enabled ? profile.deEsserGain : 0;
      chain.deEsserHigh.gain.value = enabled ? (profile.deEsserGain * 0.7) : 0;
    });

    console.log(`🎛️ AudioProcessor: de-esser ${enabled ? 'enabled' : 'disabled'}`);
  }

  /**
   * Resume AudioContext if suspended (e.g. after lock screen / background)
   */
  resumeContext(): void {
    if (this.ctx && this.ctx.state === 'suspended') {
      this.ctx.resume().catch((e) => console.warn('🎛️ AudioProcessor: resume failed:', e));
    }
  }

  /**
   * Disconnect an element from the processing chain
   */
  disconnectElement(audio: HTMLAudioElement): void {
    const chain = this.connected.get(audio);
    if (!chain) return;

    try {
      chain.source.disconnect();
      chain.highPass.disconnect();
      chain.lowShelf.disconnect();
      chain.peaking.disconnect();
      chain.highShelf.disconnect();
      chain.deEsser.disconnect();
      chain.deEsserHigh.disconnect();
      chain.compressor.disconnect();
    } catch {
      // Nodes may already be disconnected
    }

    this.connected.delete(audio);
    console.log('🎛️ AudioProcessor: disconnected element');
  }
}
